import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BedDouble, Bath, Home, ShowerHead, Flame, Car, Lock, ShieldCheck, WashingMachine, ArrowRight, HeartHandshake, Sparkles, MapPin, Mail, Loader2 } from "lucide-react";
import { api, getReturnToken } from "@/lib/api";
import Page from "@/components/Page";
import { PrimaryLogo } from "@/components/Brand";
import { LANDING } from "@/constants/testIds";
import { toast } from "sonner";

export default function Landing() {
  const [property, setProperty] = useState(null);
  const navigate = useNavigate();
  const hasResume = !!getReturnToken();

  useEffect(() => {
    api.get("/property").then((r) => setProperty(r.data)).catch(() => {});
  }, []);

  const startApply = () => navigate("/apply");

  return (
    <Page>
      {/* HERO */}
      <section className="relative overflow-hidden p2p-hero-wash">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-16 sm:pt-16 sm:pb-24">
          <div className="flex justify-center sm:justify-start mb-6">
            <PrimaryLogo maxWidth={320} />
          </div>
          <span className="p2p-chip">
            <Sparkles className="w-3 h-3" /> A welcoming, private townhome
          </span>
          <h1 className="font-serif mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-tight" style={{ color: "#0B2D59" }}>
            A place to begin again.
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed" style={{ color: "#1F2937" }}>
            Everybody&apos;s story may not look perfect on paper. This application is designed to give people a
            fair and respectful way to apply. It asks for the basic information we need and, if you choose,
            gives you space to tell us more about where you are today and what you are working toward.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <button className="p2p-btn-primary inline-flex items-center gap-2" data-testid={LANDING.startApplyBtn} onClick={startApply}>
              Start application <ArrowRight className="w-4 h-4" />
            </button>
            {hasResume && (
              <button className="p2p-btn-secondary inline-flex items-center gap-2" data-testid={LANDING.resumeBtn} onClick={() => navigate("/apply")}>
                Resume my application
              </button>
            )}
            <Link to="/criteria" className="text-sm underline ml-1" style={{ color: "#0B2D59" }} data-testid={LANDING.criteriaLink}>
              How applications are reviewed
            </Link>
            <Link to="/accommodation" className="text-sm underline ml-1" style={{ color: "#0B2D59" }} data-testid={LANDING.accommodationBtn}>
              Request an accessibility accommodation
            </Link>
          </div>
          <div className="mt-3 text-sm flex items-center gap-2" style={{ color: "#475569" }}>
            <HeartHandshake className="w-4 h-4" /> Take your time. You can save your application and return later.
          </div>
          <ResumeLinkForm />
        </div>
      </section>

      {/* PROPERTY */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-10 sm:-mt-16 relative z-10">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 p2p-card p-6 sm:p-8">
            <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: "#0B2D59" }}>About the home</h2>
            <p className="mt-3" style={{ color: "#1F2937" }}>
              A fully renovated, private, townhome-style two-bedroom rental. Quiet, secure, and brand-new throughout.
              Located near Omaha&apos;s Country Club and Benson areas.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm" style={{ color: "#0B2D59" }}>
              {[
                ["Two bedrooms", BedDouble],
                ["One full bathroom + private half-bath in primary", Bath],
                ["Fully renovated interior, new appliances", Home],
                ["Ceiling-mounted rainfall shower", ShowerHead],
                ["Electric fireplace-style heater", Flame],
                ["Dedicated off-street parking", Car],
                ["Secured entrance", Lock],
                ["Free shared laundry", WashingMachine],
                ["Separate unit, its own address — no one above or below", ShieldCheck],
              ].map(([t, Ic]) => (
                <div key={t} className="flex items-start gap-3">
                  <Ic className="w-5 h-5 mt-0.5" style={{ color: "#0B2D59" }} />
                  <span>{t}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-2 text-sm" style={{ color: "#475569" }}>
              <MapPin className="w-4 h-4" /> {property?.address || "2821 North 55th Street, Omaha, Nebraska 68104"}
            </div>
          </div>

          <aside className="p2p-card p-6 sm:p-8">
            <div className="font-serif text-xl" style={{ color: "#0B2D59" }}>The basics</div>
            <dl className="mt-4 space-y-3 text-sm">
              <Item label="Monthly rent" value={property ? `$${Number(property.monthly_rent).toLocaleString()}` : "—"} testId={LANDING.rentValue} />
              <Item label="Security deposit"
                value={property ? (property.security_deposit != null && property.security_deposit !== "" ? `$${Number(property.security_deposit).toLocaleString()}` : "Negotiable") : "—"}
                testId={LANDING.depositValue} />
              <Item label="Available" value={property?.availability_date || "—"} testId={LANDING.availability} />
              <Item label="Lease" value="Month-to-month initially" />
              <Item label="Utilities" value={property?.utilities_note || "—"} />
              <Item label="Credit check" value="No credit check required" />
              <Item label="Background check" value="No criminal background check" />
              <Item label="Required" value="Income verification and references" />
            </dl>
            {property && (property.security_deposit == null || property.security_deposit === "") && (
              <p className="text-xs mt-3" style={{ color: "#475569" }}>
                The deposit is negotiable and will be discussed before lease signing.
              </p>
            )}
            <button className="p2p-btn-primary w-full mt-6 inline-flex items-center justify-center gap-2" onClick={startApply}>
              Start application <ArrowRight className="w-4 h-4" />
            </button>
            <p className="encourage mt-4 text-sm">
              We are looking for honesty, not perfection.
            </p>
          </aside>
        </div>
      </section>

      {/* COMMITMENTS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            ["Respectful", "We treat every applicant with dignity. A real person reviews every completed application."],
            ["Voluntary story", "If you choose, share where you are today and what you are working toward — it's voluntary."],
            ["Fair housing", "We apply the same written criteria to every applicant. Equal Housing Opportunity."],
          ].map(([title, body]) => (
            <div key={title} className="p2p-card p-6">
              <div className="font-serif text-xl" style={{ color: "#0B2D59" }}>{title}</div>
              <p className="mt-2 text-sm" style={{ color: "#1F2937" }}>{body}</p>
            </div>
          ))}
        </div>
      </section>
    </Page>
  );
}

function Item({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-4 border-b pb-2" style={{ borderColor: "#E2EBF1" }}>
      <dt className="text-xs uppercase tracking-wide" style={{ color: "#475569" }}>{label}</dt>
      <dd className="text-right font-medium" data-testid={testId} style={{ color: "#0B2D59" }}>{value}</dd>
    </div>
  );
}

function ResumeLinkForm() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/applications/resume-link", { email: email.trim() });
      toast.success(data?.message || "If we have an in-progress application for that email, a resume link is on its way.");
      setOpen(false);
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send the link right now. Please try again shortly.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 text-sm">
      {!open ? (
        <button className="underline inline-flex items-center gap-1.5" style={{ color: "#0B2D59" }}
          data-testid="landing-resume-email-link" onClick={() => setOpen(true)}>
          <Mail className="w-3.5 h-3.5" /> Started on another device? Email me my resume link
        </button>
      ) : (
        <form onSubmit={send} className="flex flex-wrap items-center gap-2 mt-1" data-testid="landing-resume-email-form">
          <input type="email" className="p2p-input" style={{ maxWidth: "280px", padding: "0.5rem 0.9rem" }}
            placeholder="Email you used on your application" required
            data-testid="landing-resume-email-input"
            value={email} onChange={(e) => setEmail(e.target.value)} />
          <button type="submit" className="p2p-btn-secondary inline-flex items-center gap-2"
            style={{ padding: "0.5rem 1rem" }} disabled={busy}
            data-testid="landing-resume-email-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Sending…" : "Email my link"}
          </button>
          <button type="button" className="text-xs underline" style={{ color: "#475569" }} onClick={() => setOpen(false)}>
            Cancel
          </button>
        </form>
      )}
    </div>
  );
}
